import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { Page, StockRow } from '@elutide/shared';
import { api, ApiProblem } from '@/lib/api';
import { qk } from '@/lib/keys';
import { LIVE } from '@/app/queryClient';
import { daysLeftTone, formatDaysLeft, formatVelocity } from '@/lib/format';
import { Card, DataTable, EmptyState, ErrorState, Pill, type Column } from '@/components';

type Row = StockRow | (Omit<StockRow, 'price_cents' | 'wac_cents'> & Record<string, unknown>);

/**
 * The five SKUs closest to running out, using the same days_left metric the Stock page and
 * the inbox rule use. Hidden SKUs and bundles are excluded by the API filter, not here.
 */
export function StockAtRisk() {
  // include_bundles is deliberately absent. The API coerces the query value with
  // z.coerce.boolean, and Boolean('false') is true, so sending the string would include
  // exactly what this card must exclude. Omitting it takes the schema default of false.
  const filters = { status: 'critical,low', sort: 'days_left', limit: 5 };

  const query = useQuery({
    queryKey: qk.stock(filters),
    queryFn: () => api.get<Page<StockRow>>('/stock', { query: filters }),
    ...LIVE,
  });

  const rows = query.data?.items ?? [];

  const columns: readonly Column<Row>[] = [
    {
      id: 'name',
      header: 'Product',
      priority: 1,
      render: (row) => (
        <span>
          {String(row.name)}
          {row.size_label ? ` · ${String(row.size_label)}` : ''}
        </span>
      ),
    },
    {
      id: 'available',
      header: 'On hand',
      priority: 1,
      align: 'right',
      mono: true,
      render: (row) => <span>{String(row.available_qty)}</span>,
    },
    {
      id: 'days_left',
      header: 'Days left',
      priority: 1,
      align: 'right',
      render: (row) => {
        const days = row.days_left as number | null;
        return <Pill tone={daysLeftTone(days)}>{formatDaysLeft(days)}</Pill>;
      },
    },
    {
      id: 'velocity',
      header: 'Velocity',
      priority: 2,
      align: 'right',
      mono: true,
      render: (row) => <span>{formatVelocity(row.velocity_30d as number)} per day</span>,
    },
    {
      id: 'inbound',
      header: 'Inbound',
      priority: 2,
      render: (row) =>
        (row.inbound_qty as number) > 0 ? (
          <Pill tone="info">
            {`+${String(row.inbound_qty)}${row.inbound_eta ? ` due ${String(row.inbound_eta)}` : ''}`}
          </Pill>
        ) : (
          <span className="o-muted">none</span>
        ),
    },
  ];

  return (
    <Card
      title="Stock at risk"
      basis="woo_paid"
      asOf={rows.length > 0 ? new Date().toISOString() : null}
      ageClass="live"
      action={
        <Link to="/stock" className="o-btn o-btn--ghost o-btn--sm">
          All stock
        </Link>
      }
      id="stock-at-risk"
    >
      {query.isError ? (
        <ErrorState
          message={query.error instanceof ApiProblem ? query.error.message : 'Stock did not load.'}
          requestId={query.error instanceof ApiProblem ? query.error.shortRequestId : null}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <DataTable<Row>
          caption="SKUs closest to running out"
          columns={columns}
          rows={rows}
          rowKey={(row) => String(row.product_id)}
          loading={query.isLoading}
          emptyState={<EmptyState message="No stock at risk. Nothing is inside its lead time." />}
        />
      )}
      <p className="o-muted">
        Days left is on hand divided by 30 day velocity, floored. Zero velocity reads no sales
        30d, never infinity.
      </p>
    </Card>
  );
}
